import {
  getSelectedCustomer,
  updatePanelParticipationStatus,
  updateUser,
} from '@/app/slices/userSlice';
import { nackaUser } from '@/helpers/customerUtils';
import { trackingDataEvent } from '@/helpers/trackingHelper';
import usePanelParticipation from '@/hooks/usePanelParticipation';
import { State } from '@/types/State';
import { Box, TextField, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { PrimaryButton } from 'ui';

interface PanelInviteProps {
  setPanelFormFinished: (joined: boolean) => void;
  panelFormFinished: boolean;
}

const PanelInvite: React.FC<PanelInviteProps> = ({
  setPanelFormFinished,
  panelFormFinished,
}) => {
  const { control, handleSubmit } = useForm();
  const [email, setEmail] = useState('');
  const [showPanelInvite, setShowPanelInvite] = useState(true);

  const selectedCustomer = useSelector((state: State) =>
    getSelectedCustomer(state),
  );
  const customerNumber =
    selectedCustomer?.customerAccounts?.[0]?.customerNumber || '';

  const theme = useTheme();
  const queryClient = useQueryClient();
  const { query: panelParticipationQuery } = usePanelParticipation();

  const dispatch = useDispatch();

  if (panelFormFinished) {
    return (
      <Box
        sx={{
          textAlign: 'left',
          mt: 4,
          maxWidth: '60ch',
          margin: '0 auto',
        }}
      >
        <Typography
          variant='h5'
          component='h2'
          sx={{ pt: 4, textAlign: 'left' }}
        >
          Tack för din anmälan!
        </Typography>
        <Typography
          variant='body2'
          component='h2'
          sx={{ pt: 4, textAlign: 'left' }}
        >
          Vi kontaktar dig inom kort på {selectedCustomer?.email || email}. Då
          får du möjlighet att besvara din första enkät.
        </Typography>
        <Typography variant='body2' sx={{ pt: 2 }}>
          <Link
            to='/profile?tab="account"'
            style={{
              textDecoration: 'underline',
              color: theme.palette.secondary.dark,
            }}
          >
            Fel e-postadress? Ändra den här.
          </Link>
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {showPanelInvite && (
        <>
          <Box
            sx={{
              textAlign: 'left',
              mt: 4,
              maxWidth: '60ch',
              margin: '0 auto',
            }}
          >
            <Typography variant='body2' color='textPrimary'>
              <strong>Vill du gå med i vår kundpanel</strong> för att vara med
              och påverka utvecklingen av Mälarenergis digitala lösningar?
            </Typography>
            {!selectedCustomer?.email && (
              <Box sx={{ mt: 2 }}>
                <Typography variant='body2' color='textSecondary'>
                  Ange din e-postadress för att gå med i kundpanelen:
                </Typography>
                <Controller
                  control={control}
                  name='email'
                  defaultValue={selectedCustomer?.email || ''}
                  rules={{
                    required: 'Ange en mejladress',
                    pattern: {
                      value:
                        /^[A-ZÅÄÖa-zåäö0-9._%+-]+@[A-ZÅÄÖa-zåäö0-9.-]+\.[A-ZÅÄÖa-zåäö]{2,}$/i,
                      message: 'Ange en giltig mejladress',
                    },
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      value={field.value}
                      onChange={(e) => {
                        field.onChange(e);
                        setEmail(e.target.value);
                      }}
                      label='E-postadress'
                      placeholder='Ange din e-postadress'
                      fullWidth
                      error={!!error}
                      helperText={error ? error.message : ''}
                      sx={{
                        margin: '0 auto',
                        mt: 2,
                        mb: 2,
                      }}
                    />
                  )}
                />
              </Box>
            )}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <PrimaryButton
                onClick={handleSubmit(async () => {
                  if (selectedCustomer && !selectedCustomer?.email) {
                    dispatch(
                      updateUser(
                        {
                          email: email,
                          phoneNumber: selectedCustomer.mobilePhone,
                        },
                        selectedCustomer?.customerId,
                      ) as any,
                    );
                  }
                  trackingDataEvent('kundpanel', 'on');
                  updatePanelParticipationStatus(
                    customerNumber,
                    true,
                    nackaUser(selectedCustomer),
                  ).then((response) => {
                    queryClient.setQueryData(
                      panelParticipationQuery,
                      response.participationStatus,
                    );
                  });

                  setPanelFormFinished(true);
                })}
                fullWidth
                sx={{ mt: 2 }}
              >
                Gå med i kundpanelen
              </PrimaryButton>
              <PrimaryButton
                onClick={() => {
                  setShowPanelInvite(false);
                }}
                fullWidth
                sx={{
                  backgroundColor: 'transparent',
                  color: theme.palette.secondary.main,
                  mt: 2,
                  mb: 2,
                }}
              >
                Nej tack
              </PrimaryButton>
            </Box>
            {!selectedCustomer?.email && (
              <>
                <Typography variant='caption'>
                  E-postadressen kommer att bli mottagare av all information
                  gällande kontot hos Mälarenergi.
                </Typography>
                <Box sx={{ paddingBottom: 2 }}></Box>
              </>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default PanelInvite;
